// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Video from "@conklin/App/Components/Media/Video";
import LazyImage, { LazyImageProps } from "@conklin/MobiusOverrides/LazyImage";
import PlayCircle, { playCircleHoverString, playCircleString } from "@conklin/XDL/Icons/PlayCircle";
import { VideoAssetType } from "@conklin/XDL/types";
import { color } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { IconMemo, IconWrapper } from "@insite/mobius/Icon";
import { injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import LiteYouTubeEmbed from "react-lite-youtube-embed";
import styled, { css } from "styled-components";

export const videoWrapperCommonStyle = css`
    position: relative;
    padding-top: 0;
    overflow: hidden;
    height: 100%;
    iframe,
    object,
    embed {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
    }
`;

const VimeoVideoWrapper = styled.div`
    ${videoWrapperCommonStyle}
    ${injectCss}
`;

const YoutubeVideoWrapper = styled.div`
    ${videoWrapperCommonStyle}

    /* lite youtube embed styling */
    .yt-lite {
        background-color: ${getColor("common.black")};
        position: relative;
        display: block;
        contain: content;
        background-position: 50%;
        background-size: cover;
        cursor: pointer;
    }
    .yt-lite.lyt-activated::before {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAADGCAYAAAAT+OqFAAAAdklEQVQoz42QQQ7AIAgEF/T/D+kbq/RWAlnQyyazA4aoAB4FsBSA/bFjuF1EOL7VbrIrBuusmrt4ZZORfb6ehbWdnRHEIiITaEUKa5EJqUakRSaEYBJSCY2dEstQY7AuxahwXFrvZmWl2rh4JZ07z9dLtesfNj5q0FU3A5ObbwAAAABJRU5ErkJggg==);
        background-position: top;
        background-repeat: repeat-x;
        height: 60px;
        padding-bottom: 50px;
        width: 100%;
        transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
    }
    .yt-lite::after {
        content: "";
        display: block;
        padding-bottom: var(--aspect-ratio);
    }
    .yt-lite > iframe {
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
    }
    .yt-lite > .lty-playbtn {
        width: 75px;
        height: 75px;
        cursor: pointer;
        background: url("${playCircleString}");
        mask-size: 100%;
        background-repeat: no-repeat;
        background-size: 100%;
        border: none;
        z-index: 1;
        opacity: 1;
        transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
    }
    .yt-lite:before {
        content: "";
        position: absolute;
        width: 100%;
        height: 100%;
        transition: all 0.2s cubic-bezier(0, 0, 0.2, 1);
    }
    .yt-lite:hover:before {
        opacity: 0.4;
    }
    .yt-lite:hover > .lty-playbtn {
        width: 80px;
        height: 80px;
        background: url("${playCircleHoverString}");
    }
    .yt-lite > .lty-playbtn,
    .yt-lite > .lty-playbtn::before {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate3d(-50%, -50%, 0);
    }
    .yt-lite.lyt-activated {
        cursor: unset;
    }
    .yt-lite.lyt-activated::before,
    .yt-lite.lyt-activated > .lty-playbtn {
        opacity: 0;
        pointer-events: none;
    }
    ${injectCss}
`;

const ThumbWrapper = styled.div<{ clickable?: boolean }>`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ clickable }) => (clickable ? "pointer" : "default")};

    ${IconWrapper} {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        color: ${color("secondary")};
    }
    ${injectCss};
`;

export type VideoAssetsStyles = { container?: InjectableCss; thumb?: LazyImageProps };
type OwnProps = VideoAssetType & {
    showThumbnail?: boolean;
    isThumbnailClickable?: boolean;
    extendedStyles?: VideoAssetsStyles;
    videoParameters?: string;
    description?: string;
    uploadDate?: string;
    duration?: string;
};
const VideoAsset = ({
    name,
    type,
    url,
    showThumbnail,
    isThumbnailClickable,
    extendedStyles,
    videoParameters,
    description,
    uploadDate,
    duration,
}: OwnProps) => {
    const [videoId, setVideoId] = useState("");
    const [thumbnail, setThumbnail] = useState("");
    const [thumbnailActive, setThumbnailActive] = useState(!showThumbnail);

    const getYouTubeId = (url: string) => {
        const array = url.split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
        const Id = array[2] !== undefined ? array[2].split(/[^0-9a-z_\-]/i)[0] : array[0];
        setVideoId(Id);
        setThumbnail(`http://img.youtube.com/vi/${Id}/hqdefault.jpg`);
    };

    const getVimeoId = async (url: string) => {
        let Id = "";
        try {
            Id = new URL(url)?.pathname.match(/[0-9]/g)?.join("") || "";
        } catch {
            Id = url.match(/[0-9]/g)?.join("") || "";
        }
        setVideoId(Id);
        try {
            const res = await fetch(`https://vimeo.com/api/v2/video/${Id}.json`);
            const data = await res.json();
            setThumbnail(data?.[0].thumbnail_large || "");
        } catch (e) {
            setThumbnail("");
        }
    };

    useEffect(() => {
        if (type === "StandardVideo") {
            return;
        }
        type === "YouTube" || url.includes("youtu") ? getYouTubeId(url) : getVimeoId(url);
    }, [type, url]);

    if (showThumbnail && !thumbnailActive) {
        return (
            <ThumbWrapper
                {...extendedStyles?.thumb}
                clickable={isThumbnailClickable}
                onClick={() => {
                    if (isThumbnailClickable) {
                        setThumbnailActive(active => !active);
                    }
                }}
            >
                {thumbnail ? <LazyImage src={thumbnail} altText="thumbnail" /> : <Video src={url} controls={false} />}
                <IconMemo src={PlayCircle} size={isThumbnailClickable ? 60 : 24} />
            </ThumbWrapper>
        );
    }

    return (
        <>
            {type === "YouTube" ? (
                <YoutubeVideoWrapper {...extendedStyles?.container}>
                    <LiteYouTubeEmbed id={videoId} title={name} params={videoParameters || "autoplay=1"} />

                    <div itemScope itemType="https://schema.org/VideoObject">
                        <div itemID="https://www.youtube.com" itemType="https://schema.org/WebSite">
                            <meta itemProp="url" content={`https://www.youtube.com/embed/${videoId}`} />
                            <meta itemProp="contentUrl" content={`https://www.youtube.com/embed/${videoId}`} />
                            <meta
                                itemProp="thumbnailUrl"
                                content={`http://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
                            />
                            <meta itemProp="name" content={name} />
                            <meta itemProp="description" content={description || name} />
                            <meta itemProp="caption" content={description || name} />
                            {uploadDate ? <meta itemProp="uploadDate" content={uploadDate} /> : null}
                            {duration ? <meta itemProp="duration" content={duration} /> : null}
                        </div>
                    </div>
                </YoutubeVideoWrapper>
            ) : type === "Vimeo" ? (
                <VimeoVideoWrapper {...extendedStyles?.container}>
                    <Video
                        src={`https://player.vimeo.com/video/${videoId}?color=ff214f&byline=0&portrait=0${
                            videoParameters ? `&${videoParameters}` : ""
                        }`}
                        controls
                        autoPlay={true}
                    />

                    <div itemScope itemType="https://schema.org/VideoObject">
                        <div itemID="https://www.vimeo.com" itemType="https://schema.org/WebSite">
                            <meta itemProp="url" content={`https://vimeo.com/${videoId}`} />
                            <meta itemProp="contentUrl" content={`https://vimeo.com/${videoId}`} />
                            <meta itemProp="thumbnailUrl" content={`https://vumbnail.com/${videoId}.jpg`} />
                            <meta itemProp="name" content={name} />
                            <meta itemProp="description" content={description || name} />
                            <meta itemProp="caption" content={description || name} />
                            {uploadDate ? <meta itemProp="uploadDate" content={uploadDate} /> : null}
                            {duration ? <meta itemProp="duration" content={duration} /> : null}
                        </div>
                    </div>
                </VimeoVideoWrapper>
            ) : (
                <StyledWrapper {...extendedStyles?.container}>
                    <Video src={url} controls autoPlay />
                    <div itemScope itemType="https://schema.org/VideoObject">
                        <div itemID={url} itemType="https://schema.org/WebSite">
                            <meta itemProp="url" content={name} />
                            <meta itemProp="contentUrl" content={name} />
                            <meta itemProp="thumbnailUrl" content={url} />
                            <meta itemProp="name" content={name} />
                            <meta itemProp="description" content={description || name} />
                            <meta itemProp="caption" content={description || name} />
                            {uploadDate ? <meta itemProp="uploadDate" content={uploadDate} /> : null}
                            {duration ? <meta itemProp="duration" content={duration} /> : null}
                        </div>
                    </div>
                </StyledWrapper>
            )}
        </>
    );
};

export default VideoAsset;
